import React, { useEffect, useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap-5';
import PropTypes from 'prop-types';

import { loadLlcData, saveLlcData } from '../../helpers/local_storage';
import { offMarketAssociatedPropertiesCount } from 'bundles/connect/helpers/connect_api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import LlcDataCard from './llc_data_card';
import { llcLookup } from './api';

export default function Ownership({ owner, ownerOccupied, address, propertyId }) {
  const [loading, setLoading] = useState(false);
  const [llcData, setLlcData] = useState(null);
  const [lookupFailure, setLookupFailure] = useState(false);
  const [associatedPropertiesCount, setAssociatedPropertiesCount] = useState(0);

  const fullAddress = `${address.address}, ${address.city}, ${address.state} ${address.zip}`;

  useEffect(() => {
    setLlcData(loadLlcData(propertyId));

    offMarketAssociatedPropertiesCount(propertyId)
      .then(setAssociatedPropertiesCount)
      .catch(console.warn);
  }, []);

  const lookupLlc = () => {
    if (llcData) return;

    setLoading(true);
    llcLookup(propertyId).then((data) => {
      setLoading(false);
      if (data) {
        setLlcData(data);
        saveLlcData(propertyId, data);
      } else {
        setLookupFailure(true);
      }
    }).catch(() => setLookupFailure(true));
  };

  const lookupButton = () => {
    if (llcData || !owner.isCorporation || lookupFailure) return;

    if (lookupFailure) return (
      <Button
        className="ms-3 text-danger"
        disabled
        size="sm"
        variant="link"
      >
        No LLC Data Found
      </Button>
    );

    return (
      <Button
        className="ms-3 text-secondary"
        disabled={loading}
        size="sm"
        variant="link"
        onClick={lookupLlc}
      >
        Get LLC Info
      </Button>
    );
  };

  const tableRow = (label, value, button = (<React.Fragment/>)) => {
    if (!value) return null;

    return (
      <tr>
        <td className="fw-bold">{label}</td>
        <td>
          {value}
          {button}
        </td>
      </tr>
    );
  };

  const associatedPropertiesLabel =
    associatedPropertiesCount > 0
      ? `${associatedPropertiesCount} ${associatedPropertiesCount === 1 ? 'Property' : 'Properties'}`
      : 'No Properties Available';

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <FontAwesomeIcon className="text-secondary" icon={faCircleUser} />
        <h6 className="m-0 fw-bolder">Ownership</h6>
      </div>
      <Table>
        <tbody>
          {tableRow('Owner Name', owner.fullName, lookupButton())}
          {tableRow('Owner Type', (owner.isCorporation ? 'Company' : 'Private'))}
          {tableRow('Owner Occupies Property', (ownerOccupied ? 'Yes' : 'No'))}
          {tableRow('Owner Address', fullAddress)}
          {tableRow('Associated Properties Nationally', associatedPropertiesLabel)}
        </tbody>
      </Table>
      <LlcDataCard llcData={llcData} />
      {lookupFailure && (
        <Card>
          <Card.Header className="bg-light fw-bold">
            No LLC Match Found
          </Card.Header>
        </Card>
      )}
    </div>
  );
}

Ownership.propTypes = {
  address: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }),
  owner: PropTypes.shape({
    fullName: PropTypes.string,
    isCorporation: PropTypes.bool
  }),
  ownerOccupied: PropTypes.bool,
  propertyId: PropTypes.number
};
